import React, { useState, useEffect, useMemo, useCallback } from 'react';
// import { useTabCompletion } from '../../hooks/useTabCompletion'; // Removed
// import { GhostInput } from '../common/GhostInput'; // Removed
import { useStore } from '../../store/useStore';
import { CRAFTING_RECIPES } from '../../data/crafting';
import type { CraftingRecipe, Resource } from '../../types';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { SearchInput } from '../common/SearchInput';
import { ItemIcon } from '../common/ItemIcon';
import { VirtualRecipeList } from './VirtualRecipeList';
import { ActiveCraftingSlot } from './ActiveCraftingSlot';

export const CraftingPanel: React.FC = () => {
    const { skills, resources, crafting, startCraft, claimCraft, startAutoCraft, stopAutoCraft } = useStore();
    const [selectedCategory, setSelectedCategory] = useState<'all' | 'smelting' | 'cooking' | 'manufacturing' | 'equipment'>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [dragOverSlot, setDragOverSlot] = useState<number | null>(null);

    const categories = ['all', 'smelting', 'cooking', 'manufacturing', 'equipment'];

    const availableRecipes = useMemo(() =>
        CRAFTING_RECIPES.filter(r => selectedCategory === 'all' || r.category === selectedCategory)
            .map(r => r.name),
        [selectedCategory]
    );

    // const { handleKeyDown: handleTabKeyDown, handleInputChange, hint } = useTabCompletion(searchQuery, setSearchQuery, availableRecipes, 'includes'); // Removed

    // Auto-update effect for progress bars
    const [, setTick] = useState(0);
    useEffect(() => {
        const interval = setInterval(() => setTick(t => t + 1), 100);
        return () => clearInterval(interval);
    }, []);

    const filteredRecipes = CRAFTING_RECIPES.filter(r =>
        (selectedCategory === 'all' || r.category === selectedCategory) &&
        r.name.toLowerCase().includes(searchQuery.toLowerCase())
    ).sort((a, b) => {
        const aUnlocked = (skills.crafting?.level ?? 1) >= a.level;
        const bUnlocked = (skills.crafting?.level ?? 1) >= b.level;

        const aHasResources = Object.entries(a.input).every(([res, amt]) =>
            (resources[res as Resource] ?? 0) >= (amt as number)
        );
        const bHasResources = Object.entries(b.input).every(([res, amt]) =>
            (resources[res as Resource] ?? 0) >= (amt as number)
        );

        // Priority 1: Unlocked AND Has Resources
        if (aUnlocked && aHasResources && (!bUnlocked || !bHasResources)) return -1;
        if ((!aUnlocked || !aHasResources) && bUnlocked && bHasResources) return 1;

        // Priority 2: Unlocked (but missing resources)
        if (aUnlocked && !bUnlocked) return -1;
        if (!aUnlocked && bUnlocked) return 1;

        // Priority 3: Level (Ascending)
        if (a.level !== b.level) {
            return a.level - b.level;
        }

        return a.name.localeCompare(b.name);
    });

    const getProgress = (startTime: number, duration: number) => {
        const elapsed = Date.now() - startTime;
        return Math.min(100, (elapsed / duration) * 100);
    };

    const formatTime = (ms: number) => {
        return (ms / 1000).toFixed(1) + 's';
    };

    const handleDragStart = (e: React.DragEvent, recipeId: string) => {
        e.dataTransfer.setData('recipeId', recipeId);
    };

    const handleDragOver = (e: React.DragEvent, slotId: number) => {
        e.preventDefault();
        setDragOverSlot(slotId);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOverSlot(null);
    };

    const handleDrop = (e: React.DragEvent, slotId: number) => {
        e.preventDefault();
        setDragOverSlot(null);
        const recipeId = e.dataTransfer.getData('recipeId');
        if (recipeId) {
            startAutoCraft(slotId, recipeId);
        }
    };

    const handleCraftOnce = useCallback((recipeId: string) => {
        const availableSlot = crafting.slots.find(s => s.unlocked && !s.activeCraft);
        if (availableSlot) {
            startCraft(availableSlot.id, recipeId);
        }
    }, [crafting.slots, startCraft]);

    const currentCraftingLevel = skills.crafting?.level ?? 1;
    const currentCraftingXp = skills.crafting?.xp ?? 0;
    const nextCraftingLevelXp = 50 * Math.pow(currentCraftingLevel, 2);
    const currentCraftingLevelStartXp = 50 * Math.pow(currentCraftingLevel - 1, 2);
    const craftingXpNeeded = nextCraftingLevelXp - currentCraftingLevelStartXp;
    const craftingXpProgress = currentCraftingXp - currentCraftingLevelStartXp;
    const craftingProgressPercent = Math.min(100, Math.max(0, (craftingXpProgress / craftingXpNeeded) * 100));

    const headerAction = (
        <div className="level-badge" style={{ background: 'var(--color-skill-crafting)' }}>Lvl {currentCraftingLevel}</div>
    );

    return (
        <Card title="Crafting" headerAction={headerAction}>
            <div className="xp-container" style={{ marginBottom: '24px' }}>
                <div className="xp-bar-bg">
                    <div
                        className="xp-bar-fill"
                        style={{
                            width: `${craftingProgressPercent}%`,
                            background: 'var(--color-skill-crafting)'
                        }}
                    />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                    <span>Level {currentCraftingLevel}</span>
                    <span>{Math.floor(currentCraftingXp).toLocaleString()} / {nextCraftingLevelXp.toLocaleString()} XP</span>
                    <span>Level {currentCraftingLevel + 1}</span>
                </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
                {categories.map(cat => (
                    <Button
                        key={cat}
                        size="sm"
                        variant={selectedCategory === cat ? 'primary' : 'outline'}
                        onClick={() => setSelectedCategory(cat as any)}
                        style={{ textTransform: 'capitalize' }}
                    >
                        {cat}
                    </Button>
                ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '24px', height: 'calc(100vh - 300px)' }}>
                {/* Recipe List */}
                <div style={{
                    background: 'var(--color-bg-app)',
                    borderRadius: '8px',
                    padding: '16px',
                    border: '1px solid var(--color-border)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexShrink: 0 }}>
                        <h3 style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Recipes</h3>
                        <div style={{ position: 'relative', width: '250px' }}>
                            <SearchInput
                                placeholder="Search recipes..."
                                value={searchQuery}
                                onChange={setSearchQuery}
                                options={useMemo(() => CRAFTING_RECIPES.map(r => ({
                                    value: r.id,
                                    label: r.name,
                                    // Could add icon if we have recipe icons, maybe distinct item icon?
                                    // Recipes usually produce something. Let's look up output?
                                    // r.output is { [resource]: amount }
                                    // taking first output key
                                    icon: <ItemIcon id={Object.keys(r.output)[0] as Resource} size={20} />
                                })).sort((a, b) => a.label.localeCompare(b.label)), [])}
                                onSelect={(id) => {
                                    // Just update search text to filter to this recipe
                                    const recipe = CRAFTING_RECIPES.find(r => r.id === id);
                                    if (recipe) setSearchQuery(recipe.name);
                                }}
                            />
                        </div>
                    </div>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                        <VirtualRecipeList
                            recipes={filteredRecipes}
                            resources={resources as Record<string, number>}
                            craftingLevel={skills.crafting?.level ?? 1}
                            hasAvailableSlot={crafting.slots.some(s => s.unlocked && !s.activeCraft)}
                            onCraft={handleCraftOnce}
                            onDragStart={handleDragStart}
                        />
                    </div>
                </div>

                {/* Slots */}
                <div style={{
                    background: 'var(--color-bg-app)',
                    borderRadius: '8px',
                    padding: '16px',
                    border: '1px solid var(--color-border)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <h3 style={{ marginTop: 0, marginBottom: '16px', color: 'var(--color-text-muted)', fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>Active Slots</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', overflowY: 'auto', flex: 1, paddingRight: '4px' }}>
                        {crafting.slots.map(slot => {
                            const activeRecipe = slot.activeCraft
                                ? CRAFTING_RECIPES.find(r => r.id === slot.activeCraft!.recipeId)
                                : null;

                            const assignedRecipe = slot.assignedRecipeId
                                ? CRAFTING_RECIPES.find(r => r.id === slot.assignedRecipeId)
                                : null;

                            return (
                                <ActiveCraftingSlot
                                    key={slot.id}
                                    slot={slot}
                                    activeRecipe={activeRecipe || null}
                                    assignedRecipe={assignedRecipe || null}
                                    resources={resources as Record<Resource, number>}
                                    isDragOver={dragOverSlot === slot.id}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    onStopAuto={stopAutoCraft}
                                    onClaim={claimCraft}
                                />
                            );
                        })}
                    </div>
                </div>
            </div>
            <style>{`
                @keyframes pulse {
                    0% { opacity: 1; }
                    50% { opacity: 0.8; }
                    100% { opacity: 1; }
                }
            `}</style>
        </Card >
    );
};
